import { useEffect, useState } from 'react';
import { DropdownList } from '@cancercentrum/rcc-react';
import orderBy from 'lodash/orderBy.js';
import { useAtom } from 'jotai';
import { Button } from '@cancercentrum/rcc-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines } from '@fortawesome/free-solid-svg-icons';
import type { SparrarVDItem, Survey, SurveyAnswerWithUtskicksprojekt } from '../types.ts';
import { useGetSelectedSvarAtom } from '../atoms.ts';
import { useIPO } from '../../../hooks/useIPO.ts';
import { SeverityGrade } from './SeverityGrade.tsx';
import { shouldShowSummaryFieldHeader, SummaryField } from './SummaryField.tsx';
import { SurveyInfo } from './SurveyInfo.tsx';
import { SurveyAnswerTextModal } from './SurveyAnswerTextModal.tsx';
import { ProjectViewer } from './ProjectViewer.tsx';

export const SurveyViewer = (props: {
    survey: Survey;
    reload: () => void;
    vdSparrar: SparrarVDItem[];
}) => {
    const { survey, reload, vdSparrar } = props;
    const ipo = useIPO();
    const [visaCentrala, setVisaCentrala] = useState<boolean>(false);
    const answeredSurveys: SurveyAnswerWithUtskicksprojekt[] = orderBy(
        survey.projects.flatMap(x => x.answers.map<SurveyAnswerWithUtskicksprojekt>(a => {
            return {
                ...a,
                utskicksprojekt: x,
            };
        })),
        [x => x.endDate],
        ['desc'],
    );
    const [showText, setShowText] = useState(false);
    const fmt = (item: SurveyAnswerWithUtskicksprojekt) => {
        return (
            <div className="d-flex flex-column">
                <div>Inskickad {item.endDate}</div>
                <small className="text-muted">{item.utskicksprojekt.ipoName || item.utskicksprojekt.name}</small>
            </div>
        );
    };
    const svarAtom = useGetSelectedSvarAtom(survey.config.id);
    const [selectedItemId, setSelectedItemId] = useAtom(svarAtom);
    const patient = ipo.patient;

    // Select the newest answer the first time a project is selected.
    useEffect(() => {
        if (!selectedItemId && answeredSurveys[0]?.id) {
            setSelectedItemId(answeredSurveys[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [survey]);

    const selectedAnswer = answeredSurveys.find(x => x.id === selectedItemId);

    let olderSurveys: SurveyAnswerWithUtskicksprojekt[] = [];

    if (selectedAnswer) {
        olderSurveys = answeredSurveys.filter(x => x.endDate! <= selectedAnswer.endDate!);
    }

    const onShowText = () => {
        setShowText(true);
    };

    const visibleProjects = survey.projects.filter(x => visaCentrala || x.handledByIpo || !!x.svarRootTableId);

    return (
        <div className="mt-3">
            {survey.projects.some(x => !x.handledByIpo) && (
                <div className="mb-1 form-check">
                    <input
                        id="VisaCentralaProjekt"
                        type="checkbox"
                        className="form-check-input"
                        checked={visaCentrala}
                        onChange={e => setVisaCentrala(e.currentTarget.checked)}
                    />
                    <label htmlFor="VisaCentralaProjekt" className="form-check-label">Visa alla enkätutskick som hanteras centralt</label>
                </div>
            )}

            <table className="table table-fixed mb-4">
                <thead>
                <tr>
                    <th>Enkätutskick</th>
                    <th>Personlig kod</th>
                    <th>Besvarade enkäter</th>
                    <th>Följebrev</th>
                    <th>1177</th>
                    <th>Utskickshistorik</th>
                </tr>
                </thead>
                <tbody>
                {visibleProjects.map(proj => {
                    return (
                        <ProjectViewer key={proj.config.id} utskicksprojekt={proj} reload={reload} sparrad={!!vdSparrar.length} />
                    );
                })}
                </tbody>
            </table>

            {!!answeredSurveys.length && (
                <div>
                    <div className="d-flex align-items-center gap-2">
                        <DropdownList
                            options={answeredSurveys}
                            onChange={(id: number | null) => setSelectedItemId(id)}
                            value={selectedItemId}
                            formatter={fmt}
                        />
                        {!!selectedItemId && (
                            <div>
                                <Button onClick={onShowText}><FontAwesomeIcon icon={faFileLines} className="pe-2" />Visa svar i textform</Button>
                            </div>
                        )}
                    </div>
                    <hr />
                    {!selectedItemId && (
                        <div>
                            Välj en besvarad enkät i listan ovan.
                        </div>
                    )}

                    {!!selectedItemId && showText && (
                        <div>
                            <SurveyAnswerTextModal
                                patient={patient}
                                onHide={() => setShowText(false)}
                                answer={olderSurveys[0]}
                                surveyMetadata={survey.metadata}
                                surveyConfig={survey.config}
                            />
                        </div>
                    )}

                    {!!selectedItemId && (
                        <div>
                            {shouldShowSummaryFieldHeader(olderSurveys[0], survey.metadata, survey.config.summaryFields) && <h2>Global hälsa</h2>}
                            {survey.config.summaryFields.map((x, i) => (
                                <SummaryField
                                    key={i}
                                    answer={olderSurveys[0]}
                                    summaryField={x}
                                    metadata={survey.metadata}
                                />
                            ))}

                            {survey.config.severityGrades.map((grade, i) => (
                                <SeverityGrade
                                    key={grade.name}
                                    grade={grade}
                                    answers={olderSurveys.map(x => x)}
                                    survey={survey}
                                    showSurveyVersion={i === 0}
                                    showProjectName={survey.projects.length > 1}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!answeredSurveys.length && (
                <div>
                    <hr />

                    <p>Enkäten har ännu inte besvarats.</p>
                    <SurveyInfo surveyMetadata={survey.metadata} />
                </div>
            )}
        </div>
    );
};
